import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { fetchMemories } from "../../lib/api";
import { onPresence, presence, refreshPresence } from "../../lib/presence";
import { MemoryReel } from "./MemoryReel";
import { BenchRow } from "./BenchRow";
import { DomMontage } from "./DomMontage";
import { Eyelids, useBlink } from "./Blink";
import { ReturnControl } from "../nav/ReturnControl";
import { LovedStrip } from "./LovedStrip";
import { FlipbookLink } from "../FlipbookLink";
import { FLIPBOOK_ENDING } from "../../lib/copy";
import { SitCountdown } from "./SitCountdown";

const CODE_LIKE = /<\s*\/?\s*[a-z!?]|\bon[a-z]+\s*=|javascript:|\bscript\b|&#?\w+;|\{\s*"|\btscheck\b|\bTEST_/i;

export const presenceLine = (n) => (n <= 0 ? "You're here alone, for now" : n === 1 ? "1 other is here with you" : `${n} others are here with you`);

function useOthers() {
  const [count, setCount] = useState(presence.count);
  useEffect(() => {
    const off = onPresence(setCount);
    refreshPresence();
    const t = setInterval(refreshPresence, 7000);
    return () => {
      off();
      clearInterval(t);
    };
  }, []);
  return Math.max(0, count - 1);
}

function useNarrow() {
  const [n, setN] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const f = () => setN(window.innerWidth < 768);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return n;
}

function Bar({ side, testid, children }) {
  return (
    <motion.div
      data-testid={testid}
      initial={{ scaleY: 0 }}
      animate={{ scaleY: 1 }}
      exit={{ scaleY: 0 }}
      transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
      className={`relative z-10 flex h-[11vh] max-h-[112px] min-h-[60px] shrink-0 items-center justify-center bg-[#2A1D3A] px-6 ${side === "top" ? "origin-top" : "origin-bottom"}`}
    >
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 1.4 }} className="flex items-center gap-4">
        {children}
      </motion.div>
    </motion.div>
  );
}

function Empty() {
  return (
    <motion.div data-testid="memory-empty" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.8, duration: 2 }} className="absolute inset-0 flex items-center justify-center px-6">
      <div className="rounded-[28px] bg-white/60 px-8 py-7 text-center shadow-[0_20px_60px_-30px_rgba(91,58,133,0.5)] backdrop-blur-md">
        <p className="font-hand text-4xl text-[#5B3A85] sm:text-5xl">Be the first to leave a memory</p>
        <p className="mt-3 font-display text-base italic text-[#4A3660]">Open any painting and look for the little envelope.</p>
      </div>
    </motion.div>
  );
}

export default function SittingScene({ paintings, backdrop, onStand }) {
  const [memories, setMemories] = useState(null);
  const others = useOthers();
  const narrow = useNarrow();
  const flow = Math.min(narrow ? 2 : 4, Math.max(2, 1 + others));
  const line = presenceLine(others);
  const reelBlink = useRef(null);
  const eyes = useBlink(() => reelBlink.current?.());

  useEffect(() => {
    fetchMemories()
      .then((list) => setMemories(list.filter((m) => !CODE_LIKE.test(m.text))))
      .catch(() => setMemories([]));
  }, []);
  useEffect(() => {
    if (!memories || memories.length) return;
    window.__afaeBench = () => ({ phase: "empty", shown: 0, total: 0, swaps: eyes.count, showing: [] });
    return () => delete window.__afaeBench;
  }, [memories, eyes.count]);
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onStand();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onStand]);

  return (
    <motion.div
      data-testid="sitting-scene"
      data-others={others}
      data-flow={flow}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.2 }}
      className="fixed inset-0 z-[70] flex flex-col overflow-hidden"
    >
      <LovedStrip paintings={paintings} />
      {backdrop === "dom" && <DomMontage paintings={paintings} />}
      {backdrop === "hall" && <div data-testid="memory-dim" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_50%_45%,rgba(30,20,48,0.5),rgba(22,14,36,0.78))]" />}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="film-grain opacity-40" />
      </div>
      <Bar side="top" testid="letterbox-top">
        <AnimatePresence mode="wait">
          <motion.p key={line} data-testid="presence-line" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.2 }} className="pt-9 font-display text-lg italic tracking-wide text-[#F3E8FF] sm:pt-0 sm:text-xl">
            {line}
          </motion.p>
        </AnimatePresence>
      </Bar>
      <div data-testid="memory-stage" onClick={eyes.blink} className="relative min-h-0 flex-1 cursor-pointer">
        <SitCountdown />
        {memories && (memories.length ? <MemoryReel memories={memories} flow={flow} narrow={narrow} blinkRef={reelBlink} paintings={paintings} /> : <Empty />)}
      </div>
      <BenchRow others={others} narrow={narrow} />
      <Bar side="bottom" testid="letterbox-bottom">
        <button
          data-testid="stand-up-button"
          onClick={onStand}
          className="shrink-0 whitespace-nowrap rounded-full border border-[#F3E8FF]/40 px-7 py-2 font-display text-lg text-[#F3E8FF] transition-colors duration-300 hover:bg-[#F3E8FF] hover:text-[#2A1D3A]"
        >
          Stand up
        </button>
        {!narrow && <span className="font-display text-base text-[#F3E8FF]/75">or press Esc</span>}
        <FlipbookLink testid="memory-flipbook-link" className="inline-block py-2 font-display text-sm italic text-[#F3E8FF]/90 underline-offset-4 transition-colors duration-200 hover:text-white hover:underline sm:text-base">
          {FLIPBOOK_ENDING}
        </FlipbookLink>
      </Bar>
      <Eyelids closed={eyes.closed} count={eyes.count} />
      <ReturnControl onClick={onStand} tone="light" label="to the hall" pos="top-3 sm:top-4" testid="bench-return-control" />
    </motion.div>
  );
}
